import {
  CreditCard,
  Files,
  Globe,
  Home,
  Landmark,
  LayoutGrid,
  LineChart,
  List,
  MessageSquareMore,
  PieChart,
  Repeat2,
  type LucideIcon,
} from "lucide-react";
import type { AppShellTranslations } from "@/components/app/i18n/shell";

/**
 * Chaves de permissão de `usePermissions` usadas no menu. Cada item mantém exatamente a mesma
 * chave do `AppSidebar` anterior (Cyber C1); as rotas continuam protegidas por `PermissionRoute`.
 */
export type NavPermissionKey =
  | "canViewDashboard"
  | "canViewCompanies"
  | "canViewReports"
  | "canViewCollections"
  | "canViewBilling"
  | "canViewSettings";

export type PermissionFlags = Record<NavPermissionKey, boolean>;

type NavLabelKey = keyof AppShellTranslations["sidebar"]["nav"];
type NavGroupKey = keyof AppShellTranslations["sidebar"]["groups"];

export interface NavChildConfig {
  labelKey: NavLabelKey;
  path: string;
  permissionKey?: NavPermissionKey;
}

export interface NavItemConfig extends NavChildConfig {
  icon: LucideIcon;
  trackingEvent?: string;
  children?: readonly NavChildConfig[];
}

export interface NavGroupConfig {
  groupKey: NavGroupKey;
  items: readonly NavItemConfig[];
}

export const NAV_GROUPS: readonly NavGroupConfig[] = [
  {
    groupKey: "overview",
    items: [
      { labelKey: "dashboard", icon: Home, path: "/app", permissionKey: "canViewDashboard" },
    ],
  },
  {
    groupKey: "portfolio",
    items: [
      {
        labelKey: "companies",
        icon: Landmark,
        path: "/app/companies",
        permissionKey: "canViewCompanies",
        children: [
          { labelKey: "buyers", path: "/app/companies?type=Buyer", permissionKey: "canViewCompanies" },
          { labelKey: "suppliers", path: "/app/companies?type=Supplier", permissionKey: "canViewCompanies" },
        ],
      },
      {
        labelKey: "portfolioUpdates",
        icon: Repeat2,
        path: "/app/portfolio-updates",
        permissionKey: "canViewCompanies",
      },
    ],
  },
  {
    groupKey: "riskAnalysis",
    items: [
      { labelKey: "sectorRisk", icon: PieChart, path: "/app/sector-risk", permissionKey: "canViewDashboard" },
      { labelKey: "riskMap", icon: Globe, path: "/app/risk-map", permissionKey: "canViewCompanies" },
      { labelKey: "comparison", icon: LineChart, path: "/app/comparison", permissionKey: "canViewDashboard" },
      {
        labelKey: "simulador360",
        icon: LayoutGrid,
        path: "/app/simulador-360",
        permissionKey: "canViewDashboard",
        trackingEvent: "simulador_360_menu_click",
      },
    ],
  },
  {
    groupKey: "finance",
    items: [
      { labelKey: "reports", icon: Files, path: "/app/reports", permissionKey: "canViewReports" },
      { labelKey: "collections", icon: List, path: "/app/collections", permissionKey: "canViewCollections" },
      { labelKey: "billing", icon: CreditCard, path: "/app/billing", permissionKey: "canViewBilling" },
    ],
  },
];

/** Ajuda e suporte (S8): mesma rota e mesma regra (sem permissionKey) do item "Suporte" anterior. */
export const HELP_ITEM: NavItemConfig = {
  labelKey: "helpAndSupport",
  icon: MessageSquareMore,
  path: "/app/support",
};

/** Nova consulta / ⌘K (S4): mesma permissão do `PermissionRoute` de `/app/companies/new` em App.tsx. */
export const NEW_QUERY_PATH = "/app/companies/new";
export const NEW_QUERY_PERMISSION: NavPermissionKey = "canViewCompanies";

/** Configurações da organização no menu do usuário (S11): mesma chave do item anterior. */
export const SETTINGS_PERMISSION: NavPermissionKey = "canViewSettings";

export function isAllowed(flags: PermissionFlags, key?: NavPermissionKey): boolean {
  return key === undefined || flags[key] === true;
}

/** Remove itens/subitens sem permissão e grupos que ficaram vazios (C1). */
export function filterNavGroups(groups: readonly NavGroupConfig[], flags: PermissionFlags): NavGroupConfig[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items
        .filter((item) => isAllowed(flags, item.permissionKey))
        .map((item) =>
          item.children ? { ...item, children: item.children.filter((c) => isAllowed(flags, c.permissionKey)) } : item,
        ),
    }))
    .filter((group) => group.items.length > 0);
}

/** `location` = pathname + search. Caminho com query (ex.: `?type=Buyer`) exige o mesmo pathname e os mesmos parâmetros. */
export function isPathActive(location: string, path: string): boolean {
  const [pathname, search = ""] = location.split("?");
  const [base, query] = path.split("?");
  if (query !== undefined) {
    const current = new URLSearchParams(search);
    return pathname === base && [...new URLSearchParams(query)].every(([key, value]) => current.get(key) === value);
  }
  return base === "/app" ? pathname === "/app" : pathname.startsWith(base);
}
